// One row per task, a dot where it starts and an arrow where it ends: navy the planned range,
// red the actual one, open-ended until the task is closed.

import React, { useMemo, useRef, useState, useEffect, useCallback } from "react"
import EmptyState from "@react/shared/ui/EmptyState"
import { patchRequest, postRequest, deleteRequest } from "@react/helpers/fetch"
import { toDate, todayUTC, addDays, daysBetween, fmtISO, fmtShort, snapAxis, monthColumns, monthLabelEvery, ROW_HOVER_STYLE, useContainerWidth } from "../../shared/timeline"

const t = (key, opts) => I18n.t(`projects.gantt.${key}`, opts)

const LABEL_W     = 300
const PCT_W       = 44
const ROW_H       = 30
const HEADER_H    = 30
const PAD_X       = 16
const PAD_BOTTOM  = 26   // room for the today / contract labels under the lines
const MIN_DAY_W   = 2.5
const DOT_R       = 5
const STROKE      = 3
const HEAD_LEN    = 9     // arrowhead length along the line
const MIN_ARROW_W = DOT_R + HEAD_LEN + 4

const COLORS = {
  planned:   "#1e3a8a",   // blue-900
  actual:    "#dc2626",   // red-600
  grid:      "#e2e8f0",   // slate-200
  gridText:  "#64748b",   // slate-500
  today:     "#0f172a",   // slate-900
  contract:  "#e11d48",   // rose-600
  label:     "#0f172a",
  labelMuted:"#94a3b8",   // slate-400
  dep:       "#94a3b8",
  depHover:  "#334155",   // slate-700
  rowHover:  "#f8fafc",   // slate-50
}

const flattenTree = (tasks, depth = 0, out = []) => {
  tasks.forEach(task => {
    out.push({ ...task, depth, isParent: (task.children || []).length > 0 })
    if (task.children?.length) flattenTree(task.children, depth + 1, out)
  })
  return out
}

const computeAxis = (rows, project) => {
  const dates = [todayUTC()]
  rows.forEach(task => {
    [task.start_date, task.end_date, task.actual_start_date, task.actual_end_date].forEach(d => {
      const date = toDate(d); if (date) dates.push(date)
    })
  })
  ;[project.start_date, project.end_date].forEach(d => { const date = toDate(d); if (date) dates.push(date) })
  return snapAxis(dates, 60)
}

const Mark = ({ x1, x2, y, color, open = false, onPointerDown, title }) => {
  const width  = x2 == null ? 0 : x2 - x1
  const arrow  = width >= MIN_ARROW_W
  const shaftEnd = open ? x2 : x2 - HEAD_LEN + 1
  return (
    <g style={{ cursor: onPointerDown ? "grab" : "default" }}
       onPointerDown={onPointerDown && (e => onPointerDown(e, "move"))}>
      {title && <title>{title}</title>}
      {arrow && (
        <>
          <line x1={x1} y1={y} x2={shaftEnd} y2={y} stroke={color} strokeWidth={STROKE} strokeLinecap="butt" />
          {!open && (
            <path d={`M${x2} ${y} L${x2 - HEAD_LEN} ${y - 5} L${x2 - HEAD_LEN} ${y + 5} Z`} fill={color} />
          )}
        </>
      )}
      <circle cx={x1} cy={y} r={DOT_R} fill={color} />
      {/* Invisible handles over both ends: the dot resizes the start, the head the end. */}
      {onPointerDown && (
        <>
          <rect x={x1 - 7} y={y - 9} width={14} height={18} fill="transparent" style={{ cursor: "ew-resize" }}
                onPointerDown={e => { e.stopPropagation(); onPointerDown(e, "resize-l") }} />
          {/* An open line ends at today, not at a date of its own: nothing to resize there. */}
          {arrow && !open && (
            <rect x={x2 - 10} y={y - 9} width={14} height={18} fill="transparent" style={{ cursor: "ew-resize" }}
                  onPointerDown={e => { e.stopPropagation(); onPointerDown(e, "resize-r") }} />
          )}
        </>
      )}
    </g>
  )
}

// Routed below the rows when the successor starts before the predecessor ends.
const depPath = (x1, y1, x2, y2) => {
  const gap = x2 - x1
  if (gap > 18) {
    const midX = x1 + Math.min(18, gap / 2)
    return `M${x1 + 2} ${y1} L${midX} ${y1} L${midX} ${y2} L${x2 - DOT_R - 3} ${y2}`
  }
  const out  = x1 + 10
  const midY = y1 + (y2 > y1 ? ROW_H / 2 : -ROW_H / 2)
  return `M${x1 + 2} ${y1} L${out} ${y1} L${out} ${midY} L${x2 - 12} ${midY} L${x2 - 12} ${y2} L${x2 - DOT_R - 3} ${y2}`
}

const Legend = () => (
  <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-[11.5px] text-slate-500">
    {[["planned", COLORS.planned], ["actual", COLORS.actual]].map(([key, color]) => (
      <span key={key} className="inline-flex items-center gap-1.5">
        <svg width="30" height="12" aria-hidden="true">
          <line x1="5" y1="6" x2="23" y2="6" stroke={color} strokeWidth="3" />
          <path d="M30 6 L22 2 L22 10 Z" fill={color} />
          <circle cx="5" cy="6" r="4" fill={color} />
        </svg>
        {t(`legend.${key}`)}
      </span>
    ))}
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-block h-3 w-[2px]" style={{ background: COLORS.today }} />{t("legend.today")}
    </span>
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-block h-3 w-[2px] border-l-2 border-dashed" style={{ borderColor: COLORS.contract }} />{t("legend.contract")}
    </span>
  </div>
)

const ProjectGantt = ({ project, tasks, gantt, policies }) => {
  const canManage = !!policies?.can_edit_project

  const [rows, setRows] = useState(() => flattenTree(tasks || []))
  const [deps, setDeps] = useState(gantt?.dependencies || [])
  useEffect(() => { setRows(flattenTree(tasks || [])) }, [tasks])
  useEffect(() => { setDeps(gantt?.dependencies || []) }, [gantt?.dependencies])

  const [scrollRef, containerW] = useContainerWidth()

  const axis = useMemo(() => computeAxis(rows, project), [rows, project])
  const dayW = Math.max(MIN_DAY_W, (containerW - LABEL_W - PAD_X * 2) / axis.days)
  const xFromDate = useCallback(date => PAD_X + LABEL_W + daysBetween(axis.start, toDate(date)) * dayW, [axis, dayW])

  const totalW = Math.max(containerW, PAD_X * 2 + LABEL_W + axis.days * dayW)
  const totalH = HEADER_H + rows.length * ROW_H + PAD_BOTTOM

  const months     = useMemo(() => monthColumns(axis, dayW, xFromDate), [axis, dayW, xFromDate])
  const labelEvery = monthLabelEvery(dayW)

  const today     = todayUTC()
  const todayX    = xFromDate(fmtISO(today))
  const contractX = gantt?.contract_end_date ? xFromDate(gantt.contract_end_date) : null

  const dragRef = useRef(null)

  const persistTask = (task, payload, previous) => {
    if (!gantt?.task_update_url) return
    const url = gantt.task_update_url.replace(":id", task.id)
    const revert = () => {
      setRows(prev => prev.map(row => row.id === task.id ? { ...row, ...previous } : row))
      flashAlert(t("save_error"))
    }
    patchRequest({ url, body: { project_task: payload } })
      .then(json => { if (!json?.success) revert() })
      .catch(revert)
  }

  const beginDrag = (e, task, kind, mode) => {
    if (!canManage) return
    e.preventDefault(); e.stopPropagation()
    const sKey = kind === "planned" ? "start_date" : "actual_start_date"
    const eKey = kind === "planned" ? "end_date"   : "actual_end_date"
    const origStart = toDate(task[sKey])
    const origEnd   = toDate(task[eKey]) || today
    if (!origStart) return
    dragRef.current = {
      taskId: task.id, sKey, eKey, mode, startX: e.clientX, origStart, origEnd,
      openEnded: !task[eKey],
      previous: { [sKey]: task[sKey], [eKey]: task[eKey] },
    }
    e.currentTarget.setPointerCapture?.(e.pointerId)
  }

  const onPointerMove = e => {
    const drag = dragRef.current
    if (drag) {
      const delta = Math.round((e.clientX - drag.startX) / dayW)
      let start = drag.origStart, end = drag.origEnd
      if (drag.mode === "move")     { start = addDays(start, delta); end = addDays(end, delta) }
      if (drag.mode === "resize-l") { start = addDays(start, delta); if (!drag.openEnded && start > end) start = end }
      if (drag.mode === "resize-r") { end = addDays(end, delta); if (end < start) end = start }
      setRows(prev => prev.map(row => row.id === drag.taskId
        ? { ...row, [drag.sKey]: fmtISO(start), [drag.eKey]: drag.openEnded && drag.mode !== "resize-r" ? null : fmtISO(end) }
        : row))
    }
    if (linkRef.current) {
      const rect = svgRef.current.getBoundingClientRect()
      setLinkPos({ x: e.clientX - rect.left, y: e.clientY - rect.top })
    }
  }

  const onPointerUp = e => {
    const drag = dragRef.current
    if (drag) {
      const task = rows.find(row => row.id === drag.taskId)
      const changed = task && (task[drag.sKey] !== drag.previous[drag.sKey] || task[drag.eKey] !== drag.previous[drag.eKey])
      if (changed) persistTask(task, { [drag.sKey]: task[drag.sKey], [drag.eKey]: task[drag.eKey] }, drag.previous)
      dragRef.current = null
    }
    if (linkRef.current) finishLink(e)
  }

  const svgRef  = useRef(null)
  const linkRef = useRef(null)
  const [linkPos, setLinkPos] = useState(null)
  const [hoverDep, setHoverDep] = useState(null)

  const beginLink = (e, task, x, y) => {
    if (!canManage || !gantt?.dep_create_url) return
    e.preventDefault(); e.stopPropagation()
    linkRef.current = { fromTaskId: task.id, fromX: x, fromY: y }
    setLinkPos({ x, y })
    e.currentTarget.setPointerCapture?.(e.pointerId)
  }

  const finishLink = e => {
    const rect   = svgRef.current.getBoundingClientRect()
    const index  = Math.floor((e.clientY - rect.top - HEADER_H) / ROW_H)
    const target = rows[index]
    const source = rows.find(row => row.id === linkRef.current.fromTaskId)
    linkRef.current = null; setLinkPos(null)
    if (!target || !source || target.id === source.id) return
    if (deps.some(dep => dep.predecessor_id === source.id && dep.successor_id === target.id)) return
    postRequest({ url: gantt.dep_create_url, body: { predecessor_id: source.id, successor_id: target.id, kind: "FS" } })
      .then(json => {
        if (json?.id) setDeps(prev => [...prev, json])
        else flashAlert(json?.errors?.join(", ") || t("save_error"))
      })
      .catch(() => flashAlert(t("save_error")))
  }

  const deleteDep = dep => {
    if (!canManage || !gantt?.dep_destroy_url) return
    if (!window.confirm(t("confirm_delete_dep"))) return
    deleteRequest(gantt.dep_destroy_url.replace(":id", dep.id))
      .then(json => { if (json?.success) setDeps(prev => prev.filter(d => d.id !== dep.id)) })
      .catch(() => flashAlert(t("save_error")))
  }

  const rowsById = useMemo(() => new Map(rows.map((row, index) => [row.id, { row, index }])), [rows])
  const rowY = index => HEADER_H + index * ROW_H + ROW_H / 2

  const dated = rows.some(row => row.start_date || row.actual_start_date)

  return (
    <section id="section-gantt" className="px-7 scroll-mt-24">
      <header className="mb-3 flex flex-wrap items-end justify-between gap-x-6 gap-y-2">
        <div>
          <h2 className="text-[15px] font-semibold text-slate-900">{t("title")}</h2>
          <p className="text-[12.5px] text-slate-500 mt-0.5">{canManage ? t("subtitle_editable") : t("subtitle")}</p>
        </div>
        <Legend />
      </header>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {!dated ? (
          <EmptyState size="lg">{t("empty")}</EmptyState>
        ) : (
          <div ref={scrollRef} className="overflow-x-auto"
               onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}>
            <svg ref={svgRef} width={totalW} height={totalH} style={{ display: "block", fontFamily: "inherit" }}>
              <style>{`
                .gantt-row__bg, .gantt-row__link { opacity: 0; transition: opacity .12s; }
                .gantt-row:hover .gantt-row__bg, .gantt-row:hover .gantt-row__link { opacity: 1; }
              `}</style>
              <defs>
                <marker id="gantt-dep-head" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto">
                  <path d="M 0 0 L 10 5 L 0 10 z" fill={COLORS.dep} />
                </marker>
              </defs>

              {/* Months: gridline at each start, label at the top */}
              {months.map((month, index) => (
                <g key={index}>
                  <line x1={month.x} y1={HEADER_H - 8} x2={month.x} y2={totalH - PAD_BOTTOM + 6} stroke={COLORS.grid} strokeWidth="1" />
                  {index % labelEvery === 0 && (
                    <text x={month.x + 5} y={15} fill={COLORS.gridText} fontSize="11" fontWeight="500">
                      {month.label}{month.year ? ` ${month.year}` : ""}
                    </text>
                  )}
                </g>
              ))}

              {/* Today and contract date, labelled under the chart so they never collide with the months */}
              <line x1={todayX} y1={HEADER_H - 8} x2={todayX} y2={totalH - PAD_BOTTOM + 6} stroke={COLORS.today} strokeWidth="1.5" />
              <text x={todayX} y={totalH - 7} textAnchor="middle" fill={COLORS.today} fontSize="10.5" fontWeight="600">{t("today")}</text>
              {contractX != null && (
                <>
                  <line x1={contractX} y1={HEADER_H - 8} x2={contractX} y2={totalH - PAD_BOTTOM + 6}
                        stroke={COLORS.contract} strokeWidth="1.2" strokeDasharray="4 3" />
                  <text x={contractX} y={totalH - 7}
                        textAnchor={contractX > totalW - 90 ? "end" : Math.abs(contractX - todayX) < 120 ? (contractX > todayX ? "start" : "end") : "middle"}
                        fill={COLORS.contract} fontSize="10.5" fontWeight="600">{t("contract_date")}</text>
                </>
              )}

              {/* Rows */}
              {rows.map((task, index) => {
                const y        = rowY(index)
                const pStart   = task.start_date && xFromDate(task.start_date)
                const pEnd     = task.end_date   && xFromDate(task.end_date)
                const aStart   = task.actual_start_date && xFromDate(task.actual_start_date)
                const aOpen    = !!task.actual_start_date && !task.actual_end_date
                const aEnd     = aOpen ? todayX : (task.actual_end_date && xFromDate(task.actual_end_date))
                const both     = pStart != null && aStart != null
                const plannedY = both ? y - 5 : y
                const actualY  = both ? y + 6 : y
                const pct      = Math.round(Math.max(0, Math.min(100, task.completion || 0)))
                const labelX   = PAD_X + task.depth * 14
                const maxChars = Math.max(10, Math.floor((LABEL_W - PCT_W - task.depth * 14 - 12) / 6.4))
                const title    = task.title || ""
                return (
                  <g key={task.id} className="gantt-row">
                    <rect className="gantt-row__bg" x={0} y={HEADER_H + index * ROW_H} width={totalW} height={ROW_H} fill={COLORS.rowHover} style={ROW_HOVER_STYLE} />
                    <text x={labelX} y={y + 4} fill={COLORS.label} fontSize="12.5" fontWeight={task.isParent ? 600 : 400}>
                      <title>{title}</title>
                      {title.length > maxChars ? `${title.slice(0, maxChars - 1)}…` : title}
                    </text>
                    <text x={PAD_X + LABEL_W - 12} y={y + 4} textAnchor="end" fill={COLORS.labelMuted} fontSize="11" fontWeight="500"
                          style={{ fontVariantNumeric: "tabular-nums" }}>
                      {pct}%
                    </text>

                    {pStart != null && (
                      <Mark x1={pStart} x2={pEnd} y={plannedY} color={COLORS.planned}
                            title={t("tooltip.planned", { from: fmtShort(task.start_date), to: fmtShort(task.end_date || task.start_date) })}
                            onPointerDown={canManage ? (e, mode) => beginDrag(e, task, "planned", mode) : null} />
                    )}
                    {aStart != null && (
                      <Mark x1={aStart} x2={aEnd} y={actualY} color={COLORS.actual} open={aOpen}
                            title={aOpen ? t("tooltip.actual_open", { from: fmtShort(task.actual_start_date) })
                                         : t("tooltip.actual", { from: fmtShort(task.actual_start_date), to: fmtShort(task.actual_end_date) })}
                            onPointerDown={canManage ? (e, mode) => beginDrag(e, task, "actual", mode) : null} />
                    )}

                    {/* Link handle, revealed when the row is hovered */}
                    {canManage && pEnd != null && (
                      <circle className="gantt-row__link" cx={Math.max(pEnd, pStart + DOT_R) + 10} cy={plannedY} r="4.5" fill="#fff" stroke={COLORS.planned} strokeWidth="1.5"
                              style={{ cursor: "crosshair" }}
                              onPointerDown={e => beginLink(e, task, Math.max(pEnd, pStart + DOT_R) + 10, plannedY)}>
                        <title>{t("link_hint")}</title>
                      </circle>
                    )}
                  </g>
                )
              })}

              {/* Dependencies */}
              {deps.map(dep => {
                const pre = rowsById.get(dep.predecessor_id)?.row
                const suc = rowsById.get(dep.successor_id)?.row
                if (!pre || !suc || !pre.end_date || !suc.start_date) return null
                const both1 = pre.start_date && pre.actual_start_date
                const both2 = suc.start_date && suc.actual_start_date
                const x1 = xFromDate(pre.end_date), y1 = rowY(rowsById.get(pre.id).index) - (both1 ? 5 : 0)
                const x2 = xFromDate(suc.start_date), y2 = rowY(rowsById.get(suc.id).index) - (both2 ? 5 : 0)
                const hover = hoverDep === dep.id
                return (
                  <path key={dep.id} d={depPath(x1, y1, x2, y2)} fill="none"
                        stroke={hover ? COLORS.depHover : COLORS.dep} strokeWidth={hover ? 1.8 : 1.2}
                        markerEnd="url(#gantt-dep-head)"
                        style={{ cursor: canManage ? "pointer" : "default" }}
                        onMouseEnter={() => setHoverDep(dep.id)} onMouseLeave={() => setHoverDep(null)}
                        onClick={() => deleteDep(dep)}>
                    <title>{t("dep_tooltip", { from: pre.title, to: suc.title })}</title>
                  </path>
                )
              })}

              {/* Link being drawn */}
              {linkRef.current && linkPos && (
                <line x1={linkRef.current.fromX} y1={linkRef.current.fromY} x2={linkPos.x} y2={linkPos.y}
                      stroke={COLORS.planned} strokeWidth="1.4" strokeDasharray="4 3" />
              )}
            </svg>
          </div>
        )}
      </div>
    </section>
  )
}

export default ProjectGantt
